import { IntlShape } from 'react-intl';

import { formatDateTime } from 'utils'
import { handleContainerStatus } from 'utils'

// types
import { 
    Users, 
    User,
    UserLoggedType,
    InventoryTableType, 
    ItemEditQueryType, 
    ContainersTableType,
    ContainerType,
    WarehousesTableType,
    WarehouseStatsType,
    ItemsInContainerType,
    OrdersTableType,
    OrderDetailType,
    PaymentsTableType,
    ReservationsTableType,
    ReservationDetailType,
    ItemsInOrderType,
    PaymentsInOrderType,
    PricesEquipmentExtractType,
    HomePageInTransitTypes,
    HomePageInTransitItem,
} from 'types/types';

// users ////////////////////////////////////////////////////////

// users table
export const extractDataUsers = (intl: IntlShape) => ( user: Users ) => {
    let countryName = user.country ? user.country.name : '';
    let countryNative = user.country ? user.country.nativeName : '';

    return {
        id: user?.id ?? null,
        email: user?.email ?? '',
        fullName: user?.fullName ?? '',
        phone: user?.phone ?? '',
        countryName: countryName,
        countryNative: countryNative,
        isNew: user?.isNew ?? null,
        blockedAt:
        user.blockedAt === null
            ? `${intl.formatMessage({ id: 'tableUsers.blockedAt.null' })}`
            : `${intl.formatMessage({ id: 'tableUsers.blockedAt.block' })}`,
    };
};

// user logged in
export const extractDataUserLoggedIn = (intl: IntlShape) => ( user: UserLoggedType ) => {
    const { pin, email, firstName, lastName, phone, country } = user;

    return {
        pin: pin ?? '',
        email: email ?? '',
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        phone: phone ?? '',
        country: country ?? '',
    }
}

// user profile
export const extractDataUser = (intl: IntlShape) => ( user: User ) => {
    const { id, email, firstName, lastName, phone, address, city,
        postal, country, registeredAt, role, isFreeOfCharge, blockedAt,
        reservations } = user;  

    return {
        id,
        email,
        firstName,
        lastName,
        phone,
        address,
        city,
        postal,
        country,
        registeredAt: formatDateTime(registeredAt),
        reservationsTotal: reservations.total,
        reservationslastMonth: reservations.lastMonth,
        role,
        isFreeOfCharge:
            isFreeOfCharge === false
                ? `${intl.formatMessage({ id: 'userProfile.freeOfCharge.false' })}`
                : `${intl.formatMessage({ id: 'userProfile.freeOfCharge.true' })}`,
        blockedAt:
            blockedAt === null
            ? `${intl.formatMessage({ id: 'tableUsers.blockedAt.null' })}`
            : `${intl.formatMessage({ id: 'tableUsers.blockedAt.block' })}`,
    };
  };

// payments ////////////////////////////////////////////////////////

// payments table
export const extractDataPayments = (intl: IntlShape) => ( payments: PaymentsTableType ) => {
    const { id, type, user, status, amount, finishedAt, createdAt } = payments;

    return {
        id: id ?? null,
        type: paymentType( type, intl),
        fullName: user ? user.fullName : '',
        status: statusPayment( status, intl ),
        amount: amount ? `${amount}\u00A0${intl.formatMessage({ id: 'currency' })}` : '',
        createdAt: formatDateTime( createdAt ),
        finishedAt: formatDateTime( finishedAt ),
        // checkoutUrl: payments.checkoutUrl,
    };
};


// orders ////////////////////////////////////////////////////////

// orders table
export const extractDataOrders = (intl: IntlShape) => ( orders: OrdersTableType ) => {
    const { id, user, createdAt, completedAt, status, payment, price, startPlace, finishPlace, } = orders;

    const formattedCreatedAt = formatDateTime( createdAt );
    const formattedcompletedAt = completedAt ? formatDateTime( completedAt ) : '';
    const paymentStatus = statusPayment( payment?.status, intl );

    return {
        id: id ?? null,
        fullName: user?.fullName ?? '',
        email: user?.email ?? '',
        createdAt: formattedCreatedAt,
        completedAt: formattedcompletedAt,
        status: statusOrder( status, intl ),
        paymentStatus: paymentStatus ?? '',
        price: price ?? '',
        startPlace: startPlace?.name ?? '',
        // "startPlace[name]": startPlace?.name ?? '',
        finishPlace: finishPlace ?? '',
    };
};

// orders table in user profile
export const extractDataOrdersByUser = (intl: IntlShape) => ( orders: OrdersTableType ) => {
    const { id, user, createdAt, completedAt, status, payment, price, startPlace, finishPlace, } = orders;

    const formattedCreatedAt = formatDateTime( createdAt );
    const formattedcompletedAt = completedAt ? formatDateTime( completedAt ) : '';
    return {
        id,
        email: user?.email ?? '',
        fullName: user?.fullName ?? '',
        createdAt: formattedCreatedAt,
        completedAt: formattedcompletedAt,
        status: statusOrder( status, intl ),
        price,
        startPlace: startPlace?.name ?? '',
        finishPlace,
    };
};

// order detail
export const extractDataOrder = (intl: IntlShape) => ( editOrder: OrdersTableType ) => {
    const { 
        id, user, status, price, isFreeOfCharge, 
        startPlace, finishPlace, payment,
        createdAt, completedAt,
    } = editOrder;

    const paymentStatus = statusPayment( payment?.status, intl );
    const formattedCreatedAt = formatDateTime( createdAt );
    const formattedcompletedAt = completedAt ? formatDateTime( completedAt ) : '-';
    
    return {
        id,
        email: user?.email ?? '',
        fullName: user?.fullName ?? '',
        phone: user?.phone ?? '',
        status: statusOrder( status, intl ),
        price,
        startPlace: startPlace?.name ?? '',
        finishPlace: finishPlace?.name ?? '',
        // finishPlace,
        paymentStatus: paymentStatus ?? '',
        createdAt: formattedCreatedAt,
        completedAt: formattedcompletedAt,
        isFreeOfCharge:
        isFreeOfCharge === false
            ? `${intl.formatMessage({ id: 'userProfile.freeOfCharge.false' })}`
            : `${intl.formatMessage({ id: 'userProfile.freeOfCharge.true' })}`,
    };
};

// items in order detail
export const extractDataItemsOrder = (intl: IntlShape) => ( editOrder: ItemsInOrderType ) => {
    const {
        id,
        name,photoUrl,
        price,
        rentPlace,
        returnPlace,
        status,
        returnedAt,
        rentedAt,
        variant,
    } = editOrder;
    
    const formattedReturnedAt = returnedAt ? formatDateTime(returnedAt) : '-';
    const formattedRentedAt = rentedAt ? formatDateTime(rentedAt) : '-';
    const variantItem = variant ? variant : '';
    const rentPrice = `${price}\u00A0${intl.formatMessage({ id: 'currency' })}`
    let categoryName = name ? nameInventory(name, intl) : '';
    let statusTranslate = ( status === 'rented' )
    ? `${intl.formatMessage({ id: 'equipmentsOrder.rented' })}`
    : `${intl.formatMessage({ id: 'equipmentsOrder.returned' })}`;

    return {
      id,
      name: categoryName,
      photoUrl,
      price: rentPrice,
      rentPlace: rentPlace ? rentPlace.name : '',
      returnPlace: returnPlace ? returnPlace.name : '',
      status: statusTranslate, 
      returnedAt: formattedReturnedAt,
      rentedAt: formattedRentedAt,
      variant: variantItem,
    };
};

// reservations ////////////////////////////////////////////////////////

// reservations table
export const extractDataReservations = (intl: IntlShape) => ( reservations: ReservationsTableType ) => {
    const { id, user, status, boatsCount, startAt, place, fine, isNew, isLateCancelled } = reservations;

    const formattedstartAt = formatDateTime( startAt );

    return {
        id,
        fullName: user ? user.fullName : '',
        email: user ? user.email : '',
        phone: user ? user.phone : '',
        startAt: formattedstartAt,
        status: statusReservations( status, intl ),
        boatsCount,
        place: place ? place.name : '',
        isLateCancelled,
        fine,
        isNewRes: isNew,
    };
};

// reservation detail
export const extractDataReservation = (intl: IntlShape) => ( reservations: ReservationDetailType ) => {
    const { id, status, boatsCount, startAt, createdAt, cancelledAt, place, user, fine } = reservations;

    const userName = user ? `${user.firstName}\u00A0${user.lastName}` : '';

    return {
        id,
        status: statusReservationsDetail( status, intl ),
        boatsCount,
        startAt: formatDateTime( startAt ),
        createdAt: formatDateTime( createdAt ), 
        cancelledAt: cancelledAt ? formatDateTime( cancelledAt ) : '-', 

        place: place?.name,
        
        userName: userName,
        userPhone: user?.phone ?? '',
        userEmail: user?.email ?? '',

        fineId: fine?.id ?? 0,
        fineStatus: fine ? statusFines(fine.status, intl) : '',
        fineAmount: fine?.amount ?? 0,
        fineDescription: fine?.description ?? '',
        fineCreatedAt: fine ? formatDateTime(fine.createdAt) : '',
    };
};


// fines ////////////////////////////////////////////////////////

// fines table
export const extractDataPaymentsInOrder = (intl: IntlShape) => ( payments: PaymentsInOrderType ) => {
    const { id, type, status, amount, fine } = payments;

    const translateType = (type === 'standard')
        ? `${intl.formatMessage({ id: 'tablePaymentsInOrder.typeStandard' })}`
        : `${intl.formatMessage({ id: 'tablePaymentsInOrder.typeFine' })}`
      
    return {
      id,
      type: translateType ?? '',
      status: statusPayment( status, intl ),
      amount: amount ? `${amount}\u00A0${intl.formatMessage({ id: 'currency' })}` : '',
      description: fine ? fine.description : '',
    };
};

// items ////////////////////////////////////////////////////////

// items table
export const extractDataInventory = (intl: IntlShape) => ( inventory: InventoryTableType ) => {
    const {
        id,
        notes,
        variant,
        isActive,
        category,
        container,
        inventoryId,
        uid,
        location,
        lastSeenAt,
    } = inventory;

    let categoryId = category?.id || '';
    let containerId = container?.id || '';
    let containerName = container?.name || '';
    let categoryName = category ? nameInventory(category.name, intl) : '';
    let boatId = category?.name === 'kayak' ? inventoryId : '';
    let isActiveInventory = ( isActive === true )
      ? `${intl.formatMessage({ id: 'tableInventory.statusTrue' })}`
      : `${intl.formatMessage({ id: 'tableInventory.statusFalse' })}`;  
    let locationState = handleLocationState( location, intl )
    
    return {
      id,
      boatId,
      beaconMac: uid ?? '',
      variant,
      notes,
      isActive: isActiveInventory,
      categoryId,
      name: categoryName,
      containerId,
      containerName,
      location: locationState,
      lastSeenAt: formatDateTime(lastSeenAt),
      batteryStatus: `${intl.formatMessage({ id: 'tableInventory.batteryStatusFull' })}`,
    };
};

// edit item
export const extractDataItem = (intl: IntlShape) => ( editItem: ItemEditQueryType ) => {
    const { id, inventoryId, uid, notes, variant, isActive, category, container } = editItem;

    let categoryVariant = category ? category.variants : [];
    let containerName = container ? container.name : '';
    let categoryName = category ? nameInventory(category.name, intl) : '';
    let categoryId = category ? category.id : '';
    let isActiveInventory = ( isActive === true )
      ? `${intl.formatMessage({ id: 'tableInventory.statusTrue' })}`
      : `${intl.formatMessage({ id: 'tableInventory.statusFalse' })}`;  
    
    return {
      id,
      inventoryId,
      beaconMac: uid,
      name: categoryName,
      variant,
      isActive: isActiveInventory,
      notes,
      categoryId: categoryId,
      container: containerName,
      categoryVariant,
    };
};

// containers ////////////////////////////////////////////////////////

// containers table
export const extractDataContainers = (intl: IntlShape) => ( containers: ContainersTableType ) => {
    const {
        id,
        name,
        isEnabled,
        boatsCount,
        doorStatusA,
        doorStatusB,
        updatedAt,
        items,
        equipmentCount,
        doorWarningA,
        doorWarningB,
        doorOpenedAtA,
        doorOpenedAtB,
    } = containers;
    
    let enabled = isEnabled === true 
        ? `${intl.formatMessage({ id: 'tableContainers.isEnabledTrue' })}`
        : `${intl.formatMessage({ id: 'tableContainers.isEnabledFalse' })}`;

    let doorA = doorStatus(doorStatusA, doorWarningA, intl);
    let doorB = doorStatus(doorStatusB, doorWarningB, intl);

    const doorOpenedAtMinA = getMinutesUntilOpen(doorOpenedAtA);
    const doorOpenedAtMinB = getMinutesUntilOpen(doorOpenedAtB);

    let containerStatusUpdatedAt = containerStatus(handleContainerStatus( updatedAt ), intl);

    const paddleCount = items.find((item: any) => item.category === 'paddle');
    const unclassifiedCount = items.find((item: any) => item.category === 'unclassified');
    const helmetCount = items.find((item: any) => item.category === 'helmet');
    const bagCount = items.find((item: any) => item.category === 'bag');
    const vestCount = items.find((item: any) => item.category === 'vest');

    return {
        id,
        name,
        isEnabled: enabled,
        doorStatusA: doorA,
        doorStatusB: doorB,
        boatsCount,
        paddleCount: paddleCount.total,
        vestCount: vestCount.total,
        helmetCount: helmetCount.total,
        bagCount: bagCount.total,
        equipmentCount,
        status: containerStatusUpdatedAt,
        unclassifiedCount: unclassifiedCount.total,
        updatedAt,
        doorWarningA,
        doorWarningB,
        doorOpenedAtA: doorOpenedAtA ? doorOpenedAtMinA : null,
        doorOpenedAtB: doorOpenedAtB ? doorOpenedAtMinB : null,
    };
};

// container detail
export const extractDataContainer = (intl: IntlShape) => ( container: ContainerType ) => {
    const {
        id,
        name,
        isEnabled,
        capacity,
        boatsCount,
        equipmentCount,
        doorStatusA,
        doorStatusB,
        updatedAt,
        items,
        doorWarningA,
        doorWarningB,
        doorOpenedAtA,
        doorOpenedAtB,
        lastSeenAt,
    } = container;

    let enabled = isEnabled === true 
        ? `${intl.formatMessage({ id: 'tableContainers.isEnabledTrue' })}`
        : `${intl.formatMessage({ id: 'tableContainers.isEnabledFalse' })}`;

    let doorA = doorStatus(doorStatusA, doorWarningA, intl);
    let doorB = doorStatus(doorStatusB, doorWarningB, intl);

    let containerStatusUpdatedAt = containerStatus(handleContainerStatus( updatedAt ), intl);

    const paddleCount = items.find((item: any) => item.category === 'paddle');
    const unclassifiedCount = items.find((item: any) => item.category === 'unclassified');
    const helmetCount = items.find((item: any) => item.category === 'helmet');
    const bagCount = items.find((item: any) => item.category === 'bag');
    const vestCount = items.find((item: any) => item.category === 'vest');

    const lastSeenAtTime = formatDateTime(lastSeenAt);

    return {
        id,
        isEnabled: enabled,
        name,
        capacity,
        equipmentCount,
        boatsCount,
        paddleCount: paddleCount.total,
        vestCount: vestCount.total,
        helmetCount: helmetCount.total,
        bagCount: bagCount.total,
        doorStatusA: doorA,
        doorStatusB: doorB,
        status: containerStatusUpdatedAt,
        updatedAt,
        vestVariants: vestCount.variants,
        helmetVariants: helmetCount.variants,
        doorWarningA,
        doorWarningB,
        doorOpenedAtA,
        doorOpenedAtB,
        lastSeenAt: lastSeenAtTime,
    };
};

// warehouses table
export const extractDataWarehouses = (intl: IntlShape) => ( warehouses: WarehousesTableType ) => {

    const cruiseWarehouse = warehouses.find(warehouse => warehouse.location === "cruise");
    const unknownWarehouse = warehouses.find(warehouse => warehouse.location === "unknown");
  
    const result: {
        paddleCount: number;
        unclassifiedCount: number;
        helmetCount: number;
        vestCount: number;
        bagCount: number;
        kayakCount: number;
        name: string;
        equipmentCount: number;
    }[] = [];
    // const result: WarehouseStatsType[] = [];

    if (unknownWarehouse) {
        const { items: unknownItems } = unknownWarehouse;
        const sum = unknownItems
            .map( item => item.total)
            .reduce((acc, curr) => acc + curr, 0);

        result.push({
          paddleCount: unknownItems.find(item => item.category === "paddle")?.total || 0,
          unclassifiedCount: unknownItems.find(item => item.category === "unclassified")?.total || 0,
          helmetCount: unknownItems.find(item => item.category === "helmet")?.total || 0,
          vestCount: unknownItems.find(item => item.category === "vest")?.total || 0,
          bagCount: unknownItems.find(item => item.category === "bag")?.total || 0,
          kayakCount: unknownItems.find(item => item.category === "kayak")?.total || 0,
          name: intl.formatMessage( {id: 'tableContainers.warehouses.mainWarehouse'} ),
          equipmentCount: sum,
        });
    }

    if (cruiseWarehouse) {
        const { items: cruiseItems } = cruiseWarehouse;
        const sum = cruiseItems
            .map( item => item.total)
            .reduce((acc, curr) => acc + curr, 0);

        result.push({
          paddleCount: cruiseItems.find(item => item.category === "paddle")?.total || 0,
          unclassifiedCount: cruiseItems.find(item => item.category === "unclassified")?.total || 0,
          helmetCount: cruiseItems.find(item => item.category === "helmet")?.total || 0,
          vestCount: cruiseItems.find(item => item.category === "vest")?.total || 0,
          bagCount: cruiseItems.find(item => item.category === "bag")?.total || 0,
          kayakCount: cruiseItems.find(item => item.category === "kayak")?.total || 0,
          name: intl.formatMessage( {id: 'tableContainers.warehouses.loans'} ),
          equipmentCount: sum,
        });
      }
    
    return result;
};

// container detail - items table
export const extractDataItems = (intl: IntlShape) => ( item: ItemsInContainerType ) => {
    const { id, uid, variant, notes, isActive, createdAt, updatedAt, category, inventoryId } = item;

    // let name = category ? category.name : null;
    let categoryId = category ? category.id : null;
    let categoryName = category ? nameInventory(category.name, intl) : ''
    

    return{ 
        id, 
        uid,
        boatId: inventoryId,
        variant,
        notes, 
        isActive, 
        createdAt, 
        updatedAt, 
        name: categoryName,
        categoryId,
    }
}

// prices ////////////////////////////////////////////////////////

// prices table
export const extractPricesEquipment = (intl: IntlShape) => ( item: PricesEquipmentExtractType ) => {
    const { category, firstHourFee, nextHourFee, lostFine, damageFine } = item;

    let categoryId = category ? category.id : null;
    let categoryName = category ? category.name : null;

    return{ 
        categoryId, 
        categoryName, 
        firstHourFee,
        nextHourFee, 
        lostFine, 
        damageFine,
    }
}


// stats ////////////////////////////////////////////////////////

export const extractStatsInTransit = (intl: IntlShape) => ( stats: HomePageInTransitTypes ) => {
    
    const kayakCount = stats.find((stat: HomePageInTransitItem) => stat.category === 'kayak');
    const paddleCount = stats.find((stat: HomePageInTransitItem) => stat.category === 'paddle');
    const unclassifiedCount = stats.find((stat: HomePageInTransitItem) => stat.category === 'unclassified');
    const helmetCount = stats.find((stat: HomePageInTransitItem) => stat.category === 'helmet');
    const bagCount = stats.find((stat: HomePageInTransitItem) => stat.category === 'bag');
    const vestCount = stats.find((stat: HomePageInTransitItem) => stat.category === 'vest');

    return{ 
        kayakCount: kayakCount?.total,
        paddleCount: paddleCount?.total,
        unclassifiedCount: unclassifiedCount?.total,
        helmetCount: helmetCount?.total,
        bagCount: bagCount?.total,
        vestCount: vestCount?.total,
    }
}

export const extractStatsContainers = (intl: IntlShape) => ( containersStats: ContainersTableType ) => {
    const { id, name, isEnabled, boatsCount, doorStatusA, doorStatusB, updatedAt } = containersStats;

    let containerStatusUpdatedAt = containerStatus(handleContainerStatus( updatedAt ), intl);

    return {
        id,
        name,
        isEnabled,
        doorStatusA,
        doorStatusB,
        boatsCount,
        status: containerStatusUpdatedAt,
    };
}


// translations ////////////////////////////////////////////////////////

// equipments
const nameInventory = ( name: string, intl: IntlShape ) => {
    if ( name === 'kayak' ) {
        return `${intl.formatMessage({ id: 'tableInventory.kayak' })}`;
    } else if ( name === 'vest' ) {
        return `${intl.formatMessage({ id: 'tableInventory.vest' })}`;
    } else if ( name === 'helmet' ) {
        return `${intl.formatMessage({ id: 'tableInventory.helmet' })}`;
    } else if ( name === 'bag' ) {
        return `${intl.formatMessage({ id: 'tableInventory.bag' })}`;
    } else if ( name === 'paddle' ) {
        return `${intl.formatMessage({ id: 'tableInventory.paddle' })}`;
    }
};  

const handleLocationState = ( location: string, intl: IntlShape ) => {
    if ( location === 'container') {
        return `${intl.formatMessage({ id: 'tableInventory.container' })}`
    } else if ( location === 'unknown' ) {
        return `${intl.formatMessage({ id: 'tableInventory.unknown' })}`
    } else if ( location === 'cruise' ) {
        return `${intl.formatMessage({ id: 'tableInventory.cruise' })}`
    }
}

// status of payments
const statusPayment = ( data: string, intl: IntlShape ) => {
    if ( data === 'pending' ) {
        return `${intl.formatMessage({ id: 'tablePayments.status.pending' })}`
    } else if ( data === 'processing' ) {
        return `${intl.formatMessage({ id: 'tablePayments.status.processing' })}`
    } else if ( data === 'successful' ) {
        return `${intl.formatMessage({ id: 'tablePayments.status.successful' })}`
    } else if ( data === 'expired' ) {
        return `${intl.formatMessage({ id: 'tablePayments.status.expired' })}`
    } else if ( data === 'failed' ) {
        return `${intl.formatMessage({ id: 'tablePayments.status.failed' })}`
    }
};

// status of fines
const statusFines = ( status: string, intl: IntlShape ) => {
    if ( status === 'paid' ) {
        return `${intl.formatMessage({ id: 'fines.detail.paid' })}`
    } else if ( status === 'awaiting_payment' ) {
        return `${intl.formatMessage({ id: 'fines.detail.awaiting_payment' })}`
    }
}

// status of orders
const statusOrder = ( status: string, intl: IntlShape ) => {
    if ( status === 'in_progress' ) {
        return `${intl.formatMessage({ id: 'order.detail.in_progress' })}`
    } else if ( status === 'awaiting_payment' ) {
        return `${intl.formatMessage({ id: 'order.detail.awaiting_payment' })}`
    } else if ( status === 'completed' ) {
        return `${intl.formatMessage({ id: 'order.detail.completed' })}`
    } else if ( status === 'cancelled' ) {
        return `${intl.formatMessage({ id: 'order.detail.cancelled' })}`
    }
};

// status of reservations
const statusReservations = ( status: string, intl: IntlShape ) => {
    if ( status === 'pending' ) {
        return `${intl.formatMessage({ id: 'tableReservations.pending' })}`
    } else if ( status === 'approved' ) {
        return `${intl.formatMessage({ id: 'tableReservations.approved' })}`
    } else if ( status === 'rejected' ) {
        return `${intl.formatMessage({ id: 'tableReservations.rejected' })}`
    } else if ( status === 'cancelled' ) {
        return `${intl.formatMessage({ id: 'tableReservations.cancelled' })}`
    } else if ( status === 'cancelled_by_customer' ) {
        return `${intl.formatMessage({ id: 'tableReservations.cancelledByCustomer' })}`
    } else if ( status === 'completed' ) {
        return `${intl.formatMessage({ id: 'tableReservations.completed' })}`
    } else if ( status === 'ready' ) {
        return `${intl.formatMessage({ id: 'tableReservations.ready' })}`
    }
};

const statusReservationsDetail = ( status: string, intl: IntlShape ) => {
    if ( status === 'pending' ) {
        return `${intl.formatMessage({ id: 'reservationDetail.StatusPending' })}`
    } else if ( status === 'approved' ) {
        return `${intl.formatMessage({ id: 'tableReservations.approved' })}`
    } else if ( status === 'rejected' ) {
        return `${intl.formatMessage({ id: 'tableReservations.rejected' })}`
    } else if ( status === 'cancelled_by_customer' ) {
        return `${intl.formatMessage({ id: 'reservationDetail.StatusCancelledByCustomer' })}`
    } else if ( status === 'cancelled' ) {
        return `${intl.formatMessage({ id: 'reservationDetail.StatusCancelledByAdmin' })}`
    } else if ( status === 'completed' ) {
        return `${intl.formatMessage({ id: 'tableReservations.completed' })}`
    } else if ( status === 'ready' ) {
        return `${intl.formatMessage({ id: 'tableReservations.ready' })}`
    }
};

// payment type
const paymentType = ( type: string, intl: IntlShape ) => {
    if ( type === 'standard' ) {
        return `${intl.formatMessage({ id: 'tablePayments.type.standard' })}`
    } else if ( type === 'verification' ) {
        return `${intl.formatMessage({ id: 'tablePayments.type.verification' })}`
    } else if ( type === 'new_card' ) {
        return `${intl.formatMessage({ id: 'tablePayments.type.new_card' })}`
    } else if ( type === 'fine' ) {
        return `${intl.formatMessage({ id: 'tablePayments.type.fine' })}`
    }
};

// door status
const doorStatus = ( doorStatus: string, doorWarning: boolean, intl: IntlShape ) => {
    if ( doorStatus === 'closed' ) {
        return `${intl.formatMessage({ id: 'tableContainers.door.close' })}`
    } else if ( doorStatus === 'opened' && doorWarning === false ) {
        return `${intl.formatMessage({ id: 'tableContainers.door.open' })}`;
    } else if ( doorStatus === 'opened' && doorWarning === true ) {
        return `${intl.formatMessage({ id: 'tableContainers.door.warning' })}`; 
    } else if ( doorStatus === 'unknown' ) {
        return `${intl.formatMessage({ id: 'tableContainers.door.unknown' })}`;
    }
};

// container status
const containerStatus = ( dateOnline: boolean, intl: IntlShape ) => {
    if ( dateOnline === true ) {
        return `${intl.formatMessage({ id: 'tableContainers.status.online' })}`
    } else if ( dateOnline === false ) {
        return `${intl.formatMessage({ id: 'tableContainers.status.offline' })}`;
    }
};

const getMinutesUntilOpen = (doorOpenedAt: string) => {
    const now = new Date();
    const openedDate = new Date(doorOpenedAt);
    const diffMs = openedDate.getTime() - now.getTime();
    return Math.abs(Math.floor(diffMs / 60000));
}
